import { throttle } from "lodash-es";

export interface DeviceChangeWatcherOptions {
  getActiveStream: () => MediaStream | null;
  // Snapshot of required devices when recording started (from store._detectAudioInputDevices)
  getRequiredActiveDevices: () => { deviceId: string; groupId: string }[];
  onDefaultDeviceId?: (defaultId?: string) => void;
  onMicrophoneDisconnected?: (missing: { deviceId: string; groupId: string } | null) => void;
  throttleMs?: number;
}

export function startDeviceChangeWatcher(opts: DeviceChangeWatcherOptions) {
  const { getActiveStream, getRequiredActiveDevices, onDefaultDeviceId, onMicrophoneDisconnected } =
    opts;
  const throttleMs = opts.throttleMs ?? 100;

  const handler = throttle(
    async () => {
      const stream = getActiveStream();
      if (!stream) return;

      const devices = await navigator.mediaDevices.enumerateDevices();
      const audioInputDevices = devices.filter(d => d.kind === "audioinput");
      const defaultAudioInputDeviceId = audioInputDevices.find(
        d => d.deviceId === "default"
      )?.deviceId;

      onDefaultDeviceId?.(defaultAudioInputDeviceId);

      const requiredDevices = getRequiredActiveDevices();
      const missing =
        requiredDevices.find(
          required =>
            !audioInputDevices.some(
              active => required.deviceId === active.deviceId && required.groupId === active.groupId
            )
        ) || null;

      if (missing) onMicrophoneDisconnected?.(missing);
    },
    throttleMs,
    { leading: false, trailing: true }
  );

  handler();
  navigator.mediaDevices.addEventListener("devicechange", handler);
  return () => navigator.mediaDevices.removeEventListener("devicechange", handler);
}
